import { useEffect, useMemo } from 'react'
import { usePublicBranding } from '../../hooks/usePublicBranding'

/**
 * Dynamically injects Open Graph / Twitter image meta tags into <head>,
 * pointing at the branch/company logo instead of falling back to a random
 * personal photo when the site is shared/crawled (e.g. Google search
 * results, WhatsApp/social link previews).
 *
 * Fetches GET /api/branding/logos so that whenever Super Admin updates the
 * logo, the very next page load picks up the new image automatically —
 * no separate deploy needed.
 */
function resolveApiBase() {
  const rawApiBase =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://127.0.0.1:8000'
      : window.location.origin)
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? String(rawApiBase).replace('://localhost', '://127.0.0.1')
    : rawApiBase
}

function setMetaTag(attrName, attrValue, content) {
  if (!content) return
  let el = document.querySelector(`meta[${attrName}="${attrValue}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attrName, attrValue)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

export default function SeoBranding() {
  const { branding } = usePublicBranding()
  const logoUrl = useMemo(() => {
    if (!branding.primaryLogoUrl) return ''
    if (/^https?:\/\//i.test(branding.primaryLogoUrl)) return branding.primaryLogoUrl
    const API_BASE = resolveApiBase()
    if (branding.primaryLogoUrl.startsWith('/')) {
      return `${API_BASE}${branding.primaryLogoUrl}`
    }
    return `${API_BASE}/${branding.primaryLogoUrl}`
  }, [branding.primaryLogoUrl])

  useEffect(() => {
    const companyName = branding.companyName || 'Diagnostic ERP'
    if (logoUrl) {
      setMetaTag('property', 'og:image', logoUrl)
      setMetaTag('name', 'twitter:image', logoUrl)
      setMetaTag('property', 'og:image:alt', `${companyName} logo`)
    }
    document.title = `${companyName} ERP`
    setMetaTag('property', 'og:title', companyName)
    setMetaTag('property', 'og:type', 'website')
    setMetaTag('property', 'og:url', window.location.origin)
    setMetaTag('name', 'description', `${companyName} ERP - ${branding.tagline || 'Complete Diagnostic Center Management System'}`)
    setMetaTag('name', 'twitter:card', 'summary_large_image')
    setMetaTag('name', 'twitter:title', companyName)
  }, [branding.companyName, branding.tagline, logoUrl])

  return null
}
